/**
 * Provider-agnostic crypto payment vocabulary.
 *
 * Every provider has its own status words. We normalise once, at the adapter
 * boundary, so settlement logic never branches on a provider string.
 *
 * NOTE: status is NOT the settlement gate. Money is. A provider that says
 * "finished" on a short payment, or stays "partially_paid" after the customer
 * sent the remainder in a second transaction, is common enough that gating on
 * the word instead of the arithmetic has already cost real customers their
 * goods on a sibling product.
 */
export const PAYMENT_STATUSES = [
    "waiting", // invoice open, nothing seen on chain
    "confirming", // seen, awaiting confirmations
    "partially_paid", // some money arrived, less than asked
    "finished", // provider considers it complete
    "expired", // provider window closed
    "failed", // provider gave up, cancelled or refunded
];
/**
 * Statuses where more money can still arrive at the address.
 *
 * Distinct from "unresolved", which is what the sweeper's index asks
 * (`settled_at IS NULL AND manual_review_at IS NULL`). An expired invoice is
 * not fundable but stays unresolved until someone decides what happened to the
 * money, and the two predicates must not be used interchangeably.
 */
export const FUNDABLE_PAYMENT_STATUSES = [
    "waiting",
    "confirming",
    "partially_paid",
];
export function isFundable(status) {
    return FUNDABLE_PAYMENT_STATUSES.includes(status);
}
/** What the checkout panel renders. Narrower and blunter than the real thing. */
export const CUSTOMER_PAYMENT_STATUSES = [
    "waiting",
    "confirming",
    "underpaid",
    "paid",
    "expired",
    "failed",
];
const PAYMENT_MASK = {
    waiting: "waiting",
    confirming: "confirming",
    partially_paid: "underpaid",
    finished: "paid",
    expired: "expired",
    failed: "failed",
};
/**
 * The payment twin of customerStatus(), so the worker and the admin do not each
 * grow their own switch. Same Object.hasOwn reason as there.
 */
export function customerPaymentStatus(status) {
    if (!Object.hasOwn(PAYMENT_MASK, status))
        return "waiting";
    return PAYMENT_MASK[status] ?? "waiting";
}
/**
 * Why opening a payment failed.
 *
 * Drives three things at once, which is why the list is this specific: the
 * sentence the customer reads, whether the floor learner records anything, and
 * whether the storefront should offer the same coin again.
 */
/**
 * Every way opening a payment can refuse.
 *
 * A list first and a union second, so the two cannot drift: the worker's route
 * maps this to a status code and a sentence, and a member added here without an
 * answer there would reach a customer as "something went wrong" at the moment
 * they were about to pay.
 */
export const CRYPTO_OPEN_FAILURES = [
    /** This chain will not accept an amount this small. Teaches the floor. */
    "below_min",
    /** The coin is switched off, or the provider stopped carrying it. */
    "currency_unavailable",
    /** The provider answered and refused. Asking again gets the same answer. */
    "provider_rejected",
    /** The provider did not answer. Worth another attempt shortly. */
    "provider_unavailable",
    /** The crypto rail is switched off entirely. */
    "rail_closed",
    /** No such order, or one that has already been paid for. */
    "order_not_open",
    /**
     * The current attempt is holding the customer's coins.
     *
     * Opening a new address here is how money gets stranded: the customer has
     * already sent something, and a second address invites them to send the rest
     * somewhere the first transfer will never be counted against.
     */
    "funds_on_current_attempt",
    /**
     * Coins arrived on an attempt that is now parked for a person to look at.
     *
     * Not `funds_on_current_attempt`: that one tells the customer to finish
     * paying the address, and nothing processes a transfer to a parked address
     * until somebody has looked -- so "send the rest there" is advice that
     * strands more money. The customer is sent to support instead.
     */
    "payment_in_review",
    /** This order has opened as many invoices as it is allowed to. */
    "too_many_attempts",
];
/**
 * `review`: rows parked for a human. `refunds`: delivered payments whose
 * surplus is still owed back.
 */
export const PAYMENT_LIST_VIEWS = ["review", "refunds"];
export const isPaymentListView = (v) => PAYMENT_LIST_VIEWS.includes(v);
//# sourceMappingURL=crypto.js.map